import { create } from 'zustand';
import { useAppStore } from '../state/store';

/**
 * Collaboration Store
 * 
 * Manages real-time collaboration state using localStorage for MVP
 * (production would use Socket.io/WebSocket)
 */

export interface CollaboratorInfo {
    id: string;
    name: string;
    color: string;
    cursor?: { x: number; y: number; z: number };
    selection?: string[];
    lastActive: number;
}

export interface CollabMessage {
    type: 'entity_add' | 'entity_update' | 'entity_delete' | 'selection' | 'cursor' | 'chat';
    senderId: string;
    timestamp: number;
    payload: unknown;
}

interface CollabState {
    // Connection
    isConnected: boolean;
    roomId: string | null;
    userId: string;
    userName: string;
    userColor: string;

    // Collaborators
    collaborators: Map<string, CollaboratorInfo>;

    // Chat
    chatMessages: Array<{ sender: string; message: string; timestamp: number }>;

    // Actions
    connect: (roomId: string) => void;
    disconnect: () => void;
    setUserName: (name: string) => void;

    // Sync
    broadcastEntityChange: (type: 'add' | 'update' | 'delete', entityId: string) => void;
    broadcastCursor: (position: { x: number; y: number; z: number }) => void;
    broadcastSelection: (ids: string[]) => void;
    sendChatMessage: (message: string) => void;

    // Internal
    handleMessage: (message: CollabMessage) => void;
}

/**
 * Generate random color for user
 */
function generateUserColor(): string {
    const colors = [
        '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4',
        '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F',
        '#BB8FCE', '#85C1E9', '#F8B500', '#00CED1',
    ];
    return colors[Math.floor(Math.random() * colors.length)];
}

/**
 * Generate simple user ID
 */
function generateUserId(): string {
    return `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Collaboration store using Zustand
 */
export const useCollabStore = create<CollabState>((set, get) => ({
    isConnected: false,
    roomId: null,
    userId: generateUserId(),
    userName: `User-${Math.floor(Math.random() * 1000)}`,
    userColor: generateUserColor(),
    collaborators: new Map(),
    chatMessages: [],

    connect: (roomId: string) => {
        const { userId, userName, userColor } = get();

        // Store user info in localStorage (for multi-tab simulation)
        const roomKey = `yap-room-${roomId}`;
        const existingData = localStorage.getItem(roomKey);
        const roomData = existingData ? JSON.parse(existingData) : { users: {}, messages: [] };

        // Add self to room
        roomData.users[userId] = {
            id: userId,
            name: userName,
            color: userColor,
            lastActive: Date.now(),
        };

        localStorage.setItem(roomKey, JSON.stringify(roomData));

        // Listen for changes (poll-based for localStorage)
        let lastRaw = '';
        const pollInterval = setInterval(() => {
            const data = localStorage.getItem(roomKey);
            // Raw-string guard: an unchanged room blob (the common idle case)
            // skips parse + JSON work AND the store write entirely, so the
            // CollabPanel no longer re-renders every second.
            if (!data || data === lastRaw) return;
            lastRaw = data;
            const parsed = JSON.parse(data);
            const collabs = new Map<string, CollaboratorInfo>();

            Object.entries(parsed.users).forEach(([id, info]) => {
                if (id !== userId) {
                    collabs.set(id, info as CollaboratorInfo);
                }
            });

            set({ collaborators: collabs });

            // Process new messages
            const lastMessageCount = get().chatMessages.length;
            if (parsed.messages && parsed.messages.length > lastMessageCount) {
                set({ chatMessages: parsed.messages });
            }
        }, 1000);

        // Store interval for cleanup
        (window as any).__collabInterval = pollInterval;

        set({ isConnected: true, roomId });
    },

    disconnect: () => {
        const { roomId, userId } = get();

        if (roomId) {
            const roomKey = `yap-room-${roomId}`;
            const data = localStorage.getItem(roomKey);

            if (data) {
                const parsed = JSON.parse(data);
                delete parsed.users[userId];
                localStorage.setItem(roomKey, JSON.stringify(parsed));
            }
        }

        // Clear poll interval
        if ((window as any).__collabInterval) {
            clearInterval((window as any).__collabInterval);
        }

        set({ isConnected: false, roomId: null, collaborators: new Map() });
    },

    setUserName: (name: string) => {
        set({ userName: name });

        // Update in room if connected
        const { roomId, userId } = get();
        if (roomId) {
            const roomKey = `yap-room-${roomId}`;
            const data = localStorage.getItem(roomKey);
            if (data) {
                const parsed = JSON.parse(data);
                if (parsed.users[userId]) {
                    parsed.users[userId].name = name;
                    localStorage.setItem(roomKey, JSON.stringify(parsed));
                }
            }
        }
    },

    broadcastEntityChange: (type, entityId) => {
        const { roomId, userId } = get();
        if (!roomId) return;

        const roomKey = `yap-room-${roomId}`;
        const data = localStorage.getItem(roomKey);
        if (data) {
            const parsed = JSON.parse(data);

            // Store pending change for other clients
            if (!parsed.pendingChanges) parsed.pendingChanges = [];
            parsed.pendingChanges.push({
                type: `entity_${type}`,
                senderId: userId,
                timestamp: Date.now(),
                payload: { entityId, entity: useAppStore.getState().entities[entityId] },
            });

            // Keep only last 100 changes
            parsed.pendingChanges = parsed.pendingChanges.slice(-100);

            localStorage.setItem(roomKey, JSON.stringify(parsed));
        }
    },

    broadcastCursor: (position) => {
        const { roomId, userId } = get();
        if (!roomId) return;

        const roomKey = `yap-room-${roomId}`;
        const data = localStorage.getItem(roomKey);
        if (data) {
            const parsed = JSON.parse(data);
            if (parsed.users[userId]) {
                parsed.users[userId].cursor = position;
                parsed.users[userId].lastActive = Date.now();
                localStorage.setItem(roomKey, JSON.stringify(parsed));
            }
        }
    },

    broadcastSelection: (ids) => {
        const { roomId, userId } = get();
        if (!roomId) return;

        const roomKey = `yap-room-${roomId}`;
        const data = localStorage.getItem(roomKey);
        if (data) {
            const parsed = JSON.parse(data);
            if (parsed.users[userId]) {
                parsed.users[userId].selection = ids;
                localStorage.setItem(roomKey, JSON.stringify(parsed));
            }
        }
    },

    sendChatMessage: (message) => {
        const { roomId, userName } = get();
        if (!roomId) return;

        const roomKey = `yap-room-${roomId}`;
        const data = localStorage.getItem(roomKey);
        if (data) {
            const parsed = JSON.parse(data);
            if (!parsed.messages) parsed.messages = [];
            parsed.messages.push({
                sender: userName,
                message,
                timestamp: Date.now(),
            });

            // Keep only last 100 messages
            parsed.messages = parsed.messages.slice(-100);

            localStorage.setItem(roomKey, JSON.stringify(parsed));
            set({ chatMessages: parsed.messages });
        }
    },

    handleMessage: (message) => {
        const appStore = useAppStore.getState();

        switch (message.type) {
            case 'entity_add': {
                const addPayload = message.payload as { entity: any };
                if (addPayload.entity) {
                    appStore.addEntity(addPayload.entity);
                }
                break;
            }

            case 'entity_update': {
                const updatePayload = message.payload as { entityId: string; entity: any };
                if (updatePayload.entityId) {
                    appStore.updateEntity(updatePayload.entityId, updatePayload.entity);
                }
                break;
            }

            case 'entity_delete': {
                const deletePayload = message.payload as { entityId: string };
                if (deletePayload.entityId) {
                    appStore.deleteEntity(deletePayload.entityId);
                }
                break;
            }
        }
    },
}));

/**
 * Hook for collaboration status
 */
export function useCollaboration() {
    const isConnected = useCollabStore((s) => s.isConnected);
    const roomId = useCollabStore((s) => s.roomId);
    const collaborators = useCollabStore((s) => s.collaborators);
    const connect = useCollabStore((s) => s.connect);
    const disconnect = useCollabStore((s) => s.disconnect);

    return {
        isConnected,
        roomId,
        collaboratorCount: collaborators.size,
        collaborators: Array.from(collaborators.values()),
        connect,
        disconnect,
    };
}
